import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './Hero.css';

const VARIANTS = { 1: 'aurora', 2: 'spotlight', 3: 'orbit' };
const DEFAULT_VARIANT = 'aurora';

/**
 * Reads ?hero=1|2|3 so the three signature treatments can be compared on a
 * preview deployment. Anything else falls back to the production default.
 */
export const useHeroVariant = () => {
    const { search } = useLocation();
    const requested = new URLSearchParams(search).get('hero');

    return VARIANTS[requested] || DEFAULT_VARIANT;
};

/**
 * The one place on the site that spends any boldness: the brand mark held
 * in a pool of gold light, tracking the pointer. Everything else stays quiet.
 * @author Nelson
 */
const Hero = ({ variant = DEFAULT_VARIANT, children }) => {
    const ref = useRef(null);
    const frame = useRef(0);

    const handlePointer = useCallback((event) => {
        const node = ref.current;

        if (!node) {
            return;
        }

        if (frame.current) {
            cancelAnimationFrame(frame.current);
        }

        frame.current = requestAnimationFrame(() => {
            const rect = node.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;

            node.style.setProperty('--px', x.toFixed(4));
            node.style.setProperty('--py', y.toFixed(4));
            /* The spotlight layer is fixed, so it needs viewport coordinates. */
            node.style.setProperty('--mx', `${event.clientX}px`);
            node.style.setProperty('--my', `${event.clientY}px`);
        });
    }, []);

    const handleLeave = useCallback(() => {
        const node = ref.current;

        if (node) {
            node.style.setProperty('--px', '0.5');
            node.style.setProperty('--py', '0.5');
        }
    }, []);

    useEffect(() => () => cancelAnimationFrame(frame.current), []);

    return (
        <section
            ref={ref}
            className={`hero hero--${variant}`}
            onMouseMove={handlePointer}
            onMouseLeave={handleLeave}
        >
            {/* Fixed-position layer: must stay outside .hero__stage, whose
                transform would otherwise become its containing block. */}
            <span className="hero__spot" aria-hidden="true" />

            <div className="hero__stage" aria-hidden="true">
                <span className="hero__halo" />
                <span className="hero__ring" />
                <img
                    className="hero__mark"
                    src="/logo-mark.png"
                    width="128"
                    height="128"
                    alt=""
                />
            </div>

            <div className="hero__body">{children}</div>
        </section>
    );
};

export default Hero;
